import { useMemo } from 'react';
import { Select } from '@nyce/admin-kit/ui';
import type { Environment } from '../api';
import { api } from '../session';
import { useAsync } from './common';

// production before the other kinds of the same customer (or cluster)
const sortKey = (e: Environment) =>
  `${e.discovered ? 1 : 0}|${e.discovered ? (e.clusterName ?? '') : e.customer}|${e.kind === 'production' ? 0 : 1}|${e.name}`;

/**
 * Environments that have a cluster (logs and pods pivot on them): registered ones by customer, production first, then
 * name; then the discovered namespaces nobody registered, by cluster.
 */
export function useClusterEnvironments() {
  const res = useAsync(() => api.environments(), []);
  const envs = useMemo(
    () =>
      (res.data ?? [])
        .filter((e) => !!e.clusterId)
        .sort((a, b) => sortKey(a).localeCompare(sortKey(b), undefined, { sensitivity: 'base' })),
    [res.data]
  );
  return { envs, loading: res.loading, error: res.error };
}

/** Environment select grouped by customer; discovered namespaces grouped by their cluster. */
export function EnvPicker({
  envs,
  value,
  onChange,
}: {
  envs: Environment[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <Select
      aria-label="Environment"
      className="nyk-env-picker"
      value={value}
      placeholder="Choose an environment"
      onChange={onChange}
      options={envs.map((e) => ({
        value: e.id,
        label: `${e.name} (${e.kind})`,
        group: e.discovered ? `Namespaces · ${e.clusterName ?? e.clusterId}` : e.customer || undefined,
      }))}
    />
  );
}
